type EngagementCallback = (type?: EngagementType) => void | Promise<void>;
type EngagementType = "scroll" | "click" | "timer";

interface EngagementOptions {
  scrollThreshold?: number;
  elementSelectors?: string[];
  timeThreshold?: number;
  trackOnce?: boolean;
}

interface TrackingItem {
  id: string;
  callback: EngagementCallback;
  options: EngagementOptions;
  hasEngaged: boolean;
  timerId?: number;
  dependencies?: any[];
  elementRefs?: HTMLElement[];
}

export class EngagementTracking {
  private static instance: EngagementTracking;
  private items: Map<string, TrackingItem> = new Map();
  private isInitialized: boolean = false;

  private constructor() {}

  public static getInstance(): EngagementTracking {
    if (!EngagementTracking.instance) {
      EngagementTracking.instance = new EngagementTracking();
    }
    return EngagementTracking.instance;
  }

  public initialize(): void {
    if (this.isInitialized) return;

    window.addEventListener("scroll", this.handleScroll);

    this.isInitialized = true;
    console.log("EngagementTrackingService initialized");
  }

  public dispose(): void {
    if (!this.isInitialized) return;

    window.removeEventListener("scroll", this.handleScroll);

    this.items.forEach((item) => {
      if (item.timerId) {
        window.clearTimeout(item.timerId);
      }
    });

    this.items.clear();
    this.isInitialized = false;
    console.log("EngagementTrackingService disposed");
  }

  public trackEngagement(
    id: string,
    callback: EngagementCallback,
    options: EngagementOptions = {},
    dependencies: any[] = []
  ): void {
    if (!this.isInitialized) {
      this.initialize();
    }

    const existingItem = this.items.get(id);
    if (existingItem) {
      if (
        JSON.stringify(existingItem.dependencies) !==
        JSON.stringify(dependencies)
      ) {
        this.stopTracking(id);
      } else {
        return;
      }
    }

    const defaultOptions: EngagementOptions = {
      scrollThreshold: 25,
      elementSelectors: [],
      timeThreshold: 3000,
      trackOnce: true,
    };

    const mergedOptions = { ...defaultOptions, ...options };

    const trackingItem: TrackingItem = {
      id,
      callback,
      options: mergedOptions,
      hasEngaged: false,
      dependencies,
    };

    if (
      mergedOptions.elementSelectors &&
      mergedOptions.elementSelectors.length > 0
    ) {
      const elements = document.querySelectorAll(
        mergedOptions.elementSelectors.join(", ")
      );
      trackingItem.elementRefs = Array.from(elements) as HTMLElement[];

      trackingItem.elementRefs.forEach((element: HTMLElement) => {
        element.addEventListener("click", () =>
          this.handleEngagement(id, "click")
        );
      });
    }

    if (mergedOptions.timeThreshold && mergedOptions.timeThreshold > 0) {
      trackingItem.timerId = window.setTimeout(() => {
        this.handleEngagement(id, "timer");
      }, mergedOptions.timeThreshold);
    }

    this.items.set(id, trackingItem);
  }

  public stopTracking(id: string): void {
    const item = this.items.get(id);
    if (!item) return;

    if (item.timerId) {
      window.clearTimeout(item.timerId);
    }

    if (item.elementRefs) {
      const elements = item.elementRefs;
      elements.forEach((element: HTMLElement) => {
        if (element) {
          element.removeEventListener("click", () =>
            this.handleEngagement(id, "click")
          );
        }
      });
    }

    this.items.delete(id);
  }

  public hasEngaged(id: string): boolean {
    const item = this.items.get(id);
    return item ? item.hasEngaged : false;
  }

  private handleScroll = (): void => {
    const scrollPercent =
      (window.scrollY /
        (document.documentElement.scrollHeight - window.innerHeight)) *
      100;

    this.items.forEach((item, id) => {
      if (item.hasEngaged && item.options.trackOnce) return;

      if (scrollPercent > (item.options.scrollThreshold || 25)) {
        this.handleEngagement(id, "scroll");
      }
    });
  };

  private handleEngagement = (id: string, type: EngagementType): void => {
    const item = this.items.get(id);
    if (!item) return;

    if (item.hasEngaged && item.options.trackOnce) return;

    item.hasEngaged = true;

    try {
      item.callback(type);
    } catch (error) {
      console.error(`Error in engagement callback for item ${id}:`, error);
    }
  };
}
